import { Command as CommandPrimitive } from 'cmdk';
import { ComponentProps } from 'react';

import { mergeClasses } from '@/utils';

import { SearchIcon } from '@/assets/icons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/common';

interface ICommandDialog extends ComponentProps<typeof Dialog> {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
}

interface ICommandInput extends ComponentProps<typeof CommandPrimitive.Input> {
  hasSearchIcon?: boolean;
}

const Command = ({ className, ...props }: ComponentProps<typeof CommandPrimitive>) => {
  return (
    <CommandPrimitive
      data-slot="command"
      className={mergeClasses(
        'bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md',
        className,
      )}
      {...props}
    />
  );
};

const CommandDialog = ({
  title = 'Command Palette',
  description = 'Search for a command to run...',
  children,
  className,
  showCloseButton = true,
  ...props
}: ICommandDialog) => {
  return (
    <Dialog {...props}>
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={mergeClasses('overflow-hidden p-0', className)}
        showCloseButton={showCloseButton}
      >
        <Command className="[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = ({ className, hasSearchIcon = false, ...props }: ICommandInput) => {
  return (
    <div data-slot="command-input-wrapper" className="flex h-9 items-center gap-2 pl-3 w-full">
      {hasSearchIcon && <SearchIcon className="size-4 shrink-0 opacity-50 text-neutral-7" />}
      <CommandPrimitive.Input
        data-slot="command-input"
        className={mergeClasses(
          'placeholder:text-neutral-6 flex h-10 w-full rounded-md bg-transparent text-body-sm text-primary-darkest font-medium outline-hidden disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none',
          className,
        )}
        {...props}
      />
    </div>
  );
};

const CommandList = ({ className, ...props }: ComponentProps<typeof CommandPrimitive.List>) => {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={mergeClasses(
        'max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto',
        className,
      )}
      {...props}
    />
  );
};

const CommandEmpty = ({ ...props }: ComponentProps<typeof CommandPrimitive.Empty>) => {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-3 text-center text-body-sm text-primary-darkest font-medium"
      {...props}
    />
  );
};

const CommandGroup = ({ className, ...props }: ComponentProps<typeof CommandPrimitive.Group>) => {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={mergeClasses(
        'text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-body-xs [&_[cmdk-group-heading]]:font-medium',
        className,
      )}
      {...props}
    />
  );
};

const CommandSeparator = ({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Separator>) => {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={mergeClasses('bg-border -mx-1 h-px', className)}
      {...props}
    />
  );
};

const CommandItem = ({ className, ...props }: ComponentProps<typeof CommandPrimitive.Item>) => {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={mergeClasses(
        "hover:bg-neutral-2 hover:text-primary-darkest [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 px-4 py-3 text-body-sm text-primary-darkest font-medium",
        className,
      )}
      {...props}
    />
  );
};

const CommandShortcut = ({ className, ...props }: ComponentProps<'span'>) => {
  return (
    <span
      data-slot="command-shortcut"
      className={mergeClasses(
        'text-muted-foreground ml-auto text-body-xs tracking-widest',
        className,
      )}
      {...props}
    />
  );
};

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
